import Entity from 'lib/Entity';

export interface PusherEvent {
  type: string;
  eventData: any;
}

export interface PusherEventV2 extends PusherEvent {
  eventData: {
    data?: { v2: any };
  };
  // This is something we add in the client side, only if
  // the event is classified as v2
  model?: Entity;
}

export interface PusherEventV3 extends PusherEvent {
  eventData: {
    changes?: Array<Change<any>>;
  };
}

// comes from https://github.com/remind101/r101-api/blob/master/services/chat_broadcasts/deliveries_service.rb#L631
export type Change<TContent extends StreamMessageChange | ChatMessageChange> = {
  action: string;
  type: string;
  // Chat uuid or stream uuid
  uuid: string;
  content: TContent;
};

// comes from https://github.com/remind101/r101-api/blob/master/app/representations/pusher_stream_message_representation.rb
export type StreamMessageChange = {
  stream_uuid: string;
  stream_item_uuid: string;
  // chat type TODO make it a type
  type: string;
  seq: number;
  body: string;
  body_attributes: string;
  preview: string;
  locale: string;
  urgent: boolean;
  personalized_voice_message: string;
  sender: {
    name: string;
    type: string;
    uuid: string;
  };
  can_react: boolean;
  created_at: string;
  file?: string;
  link_previews?: Array<string>;
};

export function isChatMessageChange(
  msg: ChatMessageChange | StreamMessageChange
): msg is ChatMessageChange {
  return 'chat' in msg && !('stream_uuid' in msg);
}

export function isStreamMessageChange(
  msg: ChatMessageChange | StreamMessageChange
): msg is StreamMessageChange {
  return !('chat' in msg) && 'stream_uuid' in msg;
}

// comes from https://github.com/remind101/r101-api/blob/master/app/api/v2/entities/chat_message_view.rb
// Some fields have been omitted
export type ChatMessageChange = {
  /* The uuid of the message */
  uuid: string;
  /* the type of this message */
  type: string;
  /* the ordered sequential id of the message within this chat */
  seq: number;
  /* Which chat this message is part of */
  chat: {
    uuid: string;
  };
  read_summary?: boolean;
  /* The content of the chat message */
  body: string;
  /* A preview of this message */
  preview: string;
  /* Attributes for message formatting */
  body_attributes: Array<any>;
  /* The locale of the message */
  locale: string;
  /* Who this message was from */
  sender: {
    id: string;
    uuid: string;
    display_name: string;
    /* The color of the sender */
    color: string;
    initials: string;
  };
  /* The sender of the message */
  sender2: {
    id: string;
    uuid: string;
    display_name: string;
    /* The color of the sender */
    color: string;
    initials: string;
  };
  /*  Whether you can react to the message or not */
  can_react?: boolean;

  urgent: boolean;
  /* Whether this is a personalized voice message */
  personalized_voice_message: boolean;
};

export type ActionChange<TContent extends StreamMessageChange | ChatMessageChange> = Omit<
  Change<TContent>,
  'type' | 'action'
>;
export type PusherActionV3<
  TContent extends StreamMessageChange | ChatMessageChange = StreamMessageChange
> = {
  type: string;
  changes: Array<ActionChange<TContent>>;
};
